import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const WSCtx = createContext(null);
export const useWS = () => useContext(WSCtx);

const TICK_THROTTLE_MS = 1000;
const LIVE_FLUSH_MS = 100;

function getWsUrl(token) {
  if (process.env.NODE_ENV !== "development" || !process.env.REACT_APP_BACKEND_URL) {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const base = `${proto}//${window.location.host}/api/ws`;
    return token ? `${base}?token=${encodeURIComponent(token)}` : base;
  }
  const wsBase = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + "/api/ws";
  return token ? `${wsBase}?token=${encodeURIComponent(token)}` : wsBase;
}

export function WebSocketProvider({ children }) {
  const { user } = useAuth();
  const [tick, setTick] = useState(0);
  const [registrationTick, setRegistrationTick] = useState(0);
  const [cmdTick, setCmdTick] = useState(0);
  const [lastNotif, setLastNotif] = useState(null);
  const [status, setStatus] = useState("disconnected");
  const [liveMetrics, setLiveMetrics] = useState({});
  const [liveVersion, setLiveVersion] = useState(0);
  const wsRef = useRef(null);
  const lastTickAt = useRef(0);
  const tickPending = useRef(false);
  const liveMetricsRef = useRef({});
  const flushTimer = useRef(null);

  const flushLiveMetrics = useCallback(() => {
    setLiveMetrics({ ...liveMetricsRef.current });
    setLiveVersion((v) => v + 1);
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimer.current) return;
    flushTimer.current = setTimeout(() => {
      flushTimer.current = null;
      flushLiveMetrics();
    }, LIVE_FLUSH_MS);
  }, [flushLiveMetrics]);

  const patchLiveMetrics = useCallback((accountId, metrics) => {
    if (!accountId || !metrics) return;
    liveMetricsRef.current[accountId] = {
      ...liveMetricsRef.current[accountId],
      ...metrics,
    };
    scheduleFlush();
  }, [scheduleFlush]);

  const applyMetricsBatch = useCallback((metrics) => {
    if (!metrics || typeof metrics !== "object") return;
    liveMetricsRef.current = { ...liveMetricsRef.current, ...metrics };
    scheduleFlush();
  }, [scheduleFlush]);

  const removeLiveMetrics = useCallback((accountId) => {
    if (!accountId || !liveMetricsRef.current[accountId]) return;
    delete liveMetricsRef.current[accountId];
    scheduleFlush();
  }, [scheduleFlush]);

  const hydrateLiveMetrics = useCallback(async () => {
    try {
      const { data } = await api.get("/metrics/live");
      liveMetricsRef.current = data && typeof data === "object" ? data : {};
      flushLiveMetrics();
    } catch {
      /* non-fatal — stream will populate */
    }
  }, [flushLiveMetrics]);

  const bumpTick = () => {
    const now = Date.now();
    if (now - lastTickAt.current >= TICK_THROTTLE_MS) {
      lastTickAt.current = now;
      tickPending.current = false;
      setTick((t) => t + 1);
      return;
    }
    if (!tickPending.current) {
      tickPending.current = true;
      setTimeout(() => {
        lastTickAt.current = Date.now();
        tickPending.current = false;
        setTick((t) => t + 1);
      }, TICK_THROTTLE_MS - (now - lastTickAt.current));
    }
  };

  useEffect(() => {
    if (!user || user === false) {
      setStatus("disconnected");
      liveMetricsRef.current = {};
      setLiveMetrics({});
      wsRef.current?.close();
      return;
    }

    const token = localStorage.getItem("mt5_token");
    const wsUrl = getWsUrl(token);
    let retry;
    let closed = false;

    const connect = () => {
      if (closed) return;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => {
          setStatus("connected");
          hydrateLiveMetrics();
        };
        ws.onclose = () => {
          setStatus("disconnected");
          if (!closed) retry = setTimeout(connect, 3000);
        };
        ws.onerror = () => setStatus("error");
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "metrics_batch" && msg.metrics) {
              applyMetricsBatch(msg.metrics);
            } else if (msg.type === "metrics_stream" && msg.account_id && msg.metrics) {
              patchLiveMetrics(msg.account_id, msg.metrics);
            } else if (msg.type === "ea_status" && msg.status === "OFFLINE" && msg.account_id) {
              removeLiveMetrics(msg.account_id);
              bumpTick();
            } else if (msg.type === "metrics_update" || msg.type === "ea_status") {
              bumpTick();
            } else if (msg.type === "account_registered") {
              setRegistrationTick((t) => t + 1);
              hydrateLiveMetrics();
              toast.info(`New EA connected: #${msg.account_number || "account"}`);
            } else if (msg.type === "command") {
              setCmdTick((t) => t + 1);
              if (msg.status === "failed") {
                toast.error(msg.message || "Command failed on MT5");
              } else if (msg.status === "executed") {
                bumpTick();
              }
            } else if (msg.type === "notification") {
              setLastNotif(msg.data);
              const sev = msg.data?.severity;
              const text = msg.data?.message || "New alert";
              if (sev === "danger") toast.error(text);
              else if (sev === "warning") toast.warning(text);
              else toast.info(text);
            }
          } catch {}
        };
      } catch {
        if (!closed) retry = setTimeout(connect, 3000);
      }
    };

    connect();
    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      if (flushTimer.current) clearTimeout(flushTimer.current);
      wsRef.current?.close();
    };
  }, [user, hydrateLiveMetrics, patchLiveMetrics, applyMetricsBatch, removeLiveMetrics]);

  return (
    <WSCtx.Provider value={{ tick, registrationTick, cmdTick, lastNotif, status, liveMetrics, liveVersion }}>
      {children}
    </WSCtx.Provider>
  );
}
